/**
 * operations.js — small typed helpers around GenerationStatusContext.
 *
 * Trigger sites import these instead of building op shapes ad-hoc so the
 * label/deeplink/sourceRef conventions stay consistent and the plate can
 * reason about ops without per-call special casing.
 */

import { OPERATION_KINDS } from '../state/GenerationStatusContext.jsx';
import { platformDisplayName } from './platforms.js';

// Kept as a named export for existing callers; the map lives in platforms.js.
export function platformLabel(value) {
  return platformDisplayName(value);
}

export function platformList(platforms) {
  const list = Array.from(new Set((platforms || []).filter(Boolean).map(platformLabel)));
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} + ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} + ${list[list.length - 1]}`;
}

/**
 * Start a publish-now operation. `targets` is an array of
 * `{ platform, socialAccountId }` derived from the picked accounts.
 * Returns the operation id so the caller can update/complete it.
 */
export function startPublishNowOperation(startOperation, {
  contentId,
  scheduledPostId,
  targets = [],
  navigate,
}) {
  const platforms = targets.map((t) => t.platform);
  const label = platforms.length
    ? `Publishing to ${platformList(platforms)}`
    : 'Publishing post';
  const platformStatus = {};
  platforms.forEach((p) => { platformStatus[p] = 'running'; });

  return startOperation({
    kind: OPERATION_KINDS.PUBLISH_NOW,
    label,
    subtype: 'publish_now',
    status: 'running',
    total: targets.length || 1,
    completed: 0,
    failed: 0,
    platforms,
    platformStatus,
    sourceRef: scheduledPostId
      ? { type: 'scheduledPost', id: scheduledPostId }
      : (contentId ? { type: 'contentItem', id: contentId } : null),
    deeplink: contentId ? `/app/post/${encodeURIComponent(contentId)}` : null,
    contentId,
    expectedDurationMs: 15_000,
  });
}

/**
 * Start a generation batch operation (multi-post text/image generation).
 */
export function startContentBatchOperation(startOperation, {
  batchId,
  total,
  platforms = [],
  label,
}) {
  const resolvedLabel = label || (total > 1
    ? `Generating ${total} posts${platforms.length ? ` for ${platformList(platforms)}` : ''}`
    : `Generating post${platforms.length ? ` for ${platformList(platforms)}` : ''}`);
  return startOperation({
    kind: OPERATION_KINDS.CONTENT_BATCH,
    label: resolvedLabel,
    status: 'running',
    total: total || null,
    completed: 0,
    failed: 0,
    platforms,
    sourceRef: batchId ? { type: 'batch', id: batchId } : null,
    deeplink: batchId ? `/app/batch/${batchId}` : '/app/library?filter=generating',
    batchId,
    expectedDurationMs: total ? total * 12_000 : 60_000,
  });
}

/**
 * Convenience: inspect an AI Assistant confirmAction result and start
 * the right kind of operation if the payload describes one. Returns the
 * op id (or null if the payload doesn't represent something trackable).
 */
export function trackAssistantResult(startOperation, result, fallbackLabel = 'Working…') {
  if (!result || typeof result !== 'object') return null;
  const data = result.data || result;

  // Batch generation
  if (data.batchId) {
    return startContentBatchOperation(startOperation, {
      batchId: data.batchId,
      total: data.total || data.itemsCount || data.requestedCount || null,
      platforms: data.platforms || [],
      label: data.shortStatus || data.label || fallbackLabel,
    });
  }

  // Plan regeneration
  if (data.strategyJobId) {
    return startOperation({
      kind: OPERATION_KINDS.PLAN_REGENERATE,
      label: data.shortStatus || 'Updating content plan',
      status: 'running',
      total: null,
      sourceRef: { type: 'job', id: data.strategyJobId },
      deeplink: '/app/plan',
      expectedDurationMs: 30_000,
    });
  }

  // Social connect (redirect handoff)
  if (data.socialConnectUrl || data.oauthUrl) {
    return startOperation({
      kind: OPERATION_KINDS.SOCIAL_CONNECT,
      label: 'Connecting account',
      status: 'pending',
      total: null,
      sourceRef: data.platform ? { type: 'socialAccount', id: data.platform } : null,
      deeplink: '/app/settings?tab=social',
      expectedDurationMs: 60_000,
    });
  }

  return null;
}
